// src/components/map/layers/BazaarLayer.ts
// Big Blue Bazaar marker controller.
// Owns Bazaar-specific marker/cache/geocode/popup behavior while MapView remains
// the single-map orchestrator and supplies shared collision/layout helpers.

import L from 'leaflet';
import type { MarketplaceItem } from '../../../types';
import { forwardGeocodeServer } from '../../../services/geocodeClient';

export type BazaarMapListingSelection = {
  id: string;
  item: MarketplaceItem;
};

export type BazaarOpenPayload = {
  itemId?: string;
  locationQuery?: string;
  locationKey?: string;
  itemIds?: string[];
  from?: string;
};

type MapPoint = { x: number; y: number };
type LatLng = { lat: number; lng: number };

type ListingRow = {
  id: string;
  item: MarketplaceItem;
  label: string;
  locationKey: string;
  distanceScore: number;
};

type BazaarGroup = {
  key: string;
  query: string;
  label: string;
  items: Array<{ id: string; item: MarketplaceItem }>;
};

type ResolvedGroup = {
  group: BazaarGroup;
  lat: number;
  lng: number;
};

type MarkerCacheEntry = {
  marker: any;
  signature: string;
  itemIds: string[];
};

export type BazaarLayerSyncOptions = {
  map: any;
  layer: any;
  marketplaceItems?: Record<string, MarketplaceItem | any> | Record<number, MarketplaceItem | any>;
  isDarkMode: boolean;
  userLatLng?: LatLng | null;
  clampPointToSafeBox: (point: MapPoint, size: any) => MapPoint;
  getOccupiedMapPoints: (map: any) => MapPoint[];
  resolveMapMarkerCollision: (
    desired: MapPoint,
    occupied: MapPoint[],
    size: any,
    opts?: { minDistance?: number; seed?: number; maxRadius?: number },
  ) => MapPoint;
  onSelectListing: (selection: BazaarMapListingSelection) => void;
  onOpenBazaar: (payload?: BazaarOpenPayload) => void;
};

export type BazaarLayerController = {
  sync: (options: BazaarLayerSyncOptions) => () => void;
  clear: (layer?: any | null) => void;
  closePopup: () => void;
  refreshIcons: (isDarkMode: boolean) => void;
  getMarkers: () => any[];
  resetForLayerRebuild: () => void;
  dispose: (layer?: any | null) => void;
};

const normalizePart = (value: any) => String(value ?? '').trim().replace(/\s+/g, ' ');
const normalizeKeyPart = (value: any) => normalizePart(value).toLowerCase();

const escapeHtml = (value: string) =>
  String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const createShopIcon = (listingCount: number, isDarkMode: boolean) => {
  const ICON = 46;
  const PIN_H = 56;
  const HALF = Math.round(ICON / 2);
  const dark = !!isDarkMode;
  const shell = dark ? '#4B3F72' : '#D6A72F';
  const shellDeep = dark ? '#2B2347' : '#A97513';
  const inner = dark ? '#0D1627' : '#FFFDF8';
  const ring = dark ? 'rgba(194,210,244,.60)' : 'rgba(255,255,255,.98)';
  const glyph = dark ? '#F6D77D' : '#9A6812';
  const shadow = dark
    ? '0 6px 14px rgba(0,0,0,.30),0 0 0 1px rgba(126,145,196,.10)'
    : '0 6px 12px rgba(15,23,42,.22)';

  const html = `
    <div class="roddo-shop-entry roddo-shop-bounce" style="position:relative;width:${ICON}px;height:${PIN_H}px;overflow:visible;">
      <div style="position:absolute;left:50%;top:1px;width:${ICON - 3}px;height:${ICON - 3}px;transform:translateX(-50%) rotate(-45deg);border-radius:50% 50% 50% 10%;background:linear-gradient(145deg,${shell} 0%,${shellDeep} 84%);border:2px solid ${ring};box-shadow:${shadow};">
        <div style="position:absolute;inset:4px;border-radius:999px;background:${inner};display:grid;place-items:center;transform:rotate(45deg);box-shadow:inset 0 0 0 1px ${dark ? 'rgba(184,204,245,.12)' : 'rgba(255,255,255,.65)'};overflow:hidden;">
          <svg aria-hidden="true" viewBox="0 0 24 24" width="23" height="23" fill="none" stroke="${glyph}" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" style="display:block;overflow:visible;">
            <path d="M4 9.25V20h16V9.25"></path>
            <path d="M3 9.25h18L19.25 4H4.75L3 9.25Z"></path>
            <path d="M3 9.25c0 1.35 1.02 2.45 2.28 2.45S7.55 10.6 7.55 9.25c0 1.35 1.02 2.45 2.27 2.45s2.28-1.1 2.28-2.45c0 1.35 1.02 2.45 2.27 2.45s2.28-1.1 2.28-2.45c0 1.35 1.02 2.45 2.27 2.45S21 10.6 21 9.25"></path>
            <path d="M7.25 20v-5.25h4.5V20"></path>
            <path d="M15 14.75h2.75"></path>
          </svg>
        </div>
      </div>
      ${listingCount > 1 ? `<div style="position:absolute;right:-5px;top:-4px;min-width:20px;height:20px;padding:0 5px;border-radius:999px;display:grid;place-items:center;background:${dark ? '#7657E8' : '#4EA5E9'};border:2px solid ${dark ? '#151B2D' : '#FFFFFF'};color:#fff;font-size:10px;font-weight:900;line-height:1;box-shadow:0 3px 8px rgba(15,23,42,.22);">${listingCount > 99 ? '99+' : listingCount}</div>` : ''}
    </div>
  `;

  return (L as any).divIcon({
    html,
    className: 'roddo-marketplace-map-icon',
    iconSize: [ICON, PIN_H],
    iconAnchor: [HALF, PIN_H - 2],
    popupAnchor: [0, -Math.round(ICON * 0.82)],
  });
};

const buildPopupHtml = (
  rowsForPopup: ListingRow[],
  totalCount: number,
  isDarkMode: boolean,
) => {
  const dark = !!isDarkMode;
  const rows = rowsForPopup.slice(0, 3).map(({ id, item, label }) => {
    const title = escapeHtml(String(item?.title || item?.name || 'Bazaar listing'));
    const priceCents = Number(item?.priceCents);
    const currency = String(item?.currency || 'USD').toUpperCase();
    let price = 'View listing';
    try {
      if (Number.isFinite(priceCents) && priceCents > 0) {
        price = new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(priceCents / 100);
      }
    } catch {}
    const image = String(item?.imageUrls?.[0] || item?.images?.[0] || item?.imageUrl || '').trim();
    return `<button type="button" data-roddo-bazaar-item="${escapeHtml(id)}" style="width:100%;display:flex;align-items:center;gap:10px;border:0;border-top:1px solid ${dark ? 'rgba(255,255,255,.08)' : 'rgba(45,57,87,.10)'};background:transparent;padding:10px 0;text-align:left;color:inherit;cursor:pointer;font:inherit;">${image ? `<img src="${escapeHtml(image)}" alt="" style="width:46px;height:46px;border-radius:11px;object-fit:cover;flex:none;" />` : `<div style="width:46px;height:46px;border-radius:11px;display:grid;place-items:center;background:${dark ? 'rgba(255,255,255,.06)' : 'rgba(78,165,233,.10)'};flex:none;font-size:18px;">🏪</div>`}<span style="min-width:0;flex:1;"><span style="display:block;font-size:12px;font-weight:850;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${title}</span><span style="display:block;margin-top:3px;font-size:11px;font-weight:800;opacity:.78;">${escapeHtml(price)}</span><span style="display:block;margin-top:2px;font-size:9.5px;font-weight:700;opacity:.55;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${escapeHtml(label)}</span></span><span aria-hidden="true" style="font-size:16px;opacity:.55;">›</span></button>`;
  }).join('');
  const more = Math.max(0, totalCount - Math.min(3, rowsForPopup.length));
  return `<div data-roddo-bazaar-popup="true" style="width:min(300px,calc(100vw - 42px));color:${dark ? '#F3F6FB' : '#2D3957'};font-family:inherit;"><div style="padding:2px 0 8px;"><div style="font-size:13px;font-weight:900;">Bazaar nearby</div><div style="margin-top:2px;font-size:10px;font-weight:700;opacity:.65;">Closest listings first</div></div>${rows}${more ? `<div style="padding:6px 0 0;font-size:10px;font-weight:700;opacity:.62;">+${more} more in Bazaar</div>` : ''}<button type="button" data-roddo-bazaar-open="true" style="width:100%;margin-top:9px;border:0;border-radius:10px;background:${dark ? '#7657E8' : '#4EA5E9'};color:#fff;padding:10px 12px;font-size:11px;font-weight:900;cursor:pointer;">Open Bazaar</button></div>`;
};

const buildGroups = (
  marketplaceItems?: Record<string, MarketplaceItem | any> | Record<number, MarketplaceItem | any>,
) => {
  const groups = new Map<string, BazaarGroup>();

  Object.entries(marketplaceItems || {}).forEach(([recordKey, rawItem]) => {
    const item = (rawItem || {}) as MarketplaceItem & Record<string, any>;
    const status = normalizeKeyPart(item?.status || 'active');
    if ((item as any)?.isSold || Number(item?.quantity ?? 1) <= 0) return;
    if (!['active', 'available', 'pending'].includes(status)) return;

    const city = normalizePart(item?.city);
    const region = normalizePart(item?.region);
    const countryCode = normalizePart(item?.countryCode).toUpperCase();
    if (!city) return;

    const key = [normalizeKeyPart(city), normalizeKeyPart(region), normalizeKeyPart(countryCode)]
      .filter(Boolean)
      .join('|');
    const query = [city, region, countryCode].filter(Boolean).join(', ');
    const label = [city, region].filter(Boolean).join(', ') || query;
    const id = normalizePart((item as any)?.docId || item?.id || (item as any)?.listingId || recordKey);
    if (!id || !key || !query) return;

    const existing = groups.get(key);
    if (existing) existing.items.push({ id, item });
    else groups.set(key, { key, query, label, items: [{ id, item }] });
  });

  return groups;
};

export function createBazaarLayerController(): BazaarLayerController {
  const markerCache = new Map<string, MarkerCacheEntry>();
  const geocodeCache = new Map<string, Promise<{ lat: number; lng: number } | null>>();
  let marker: any | null = null;
  let refreshToken = 0;
  let lastDarkMode = false;

  const resolveGeocode = (query: string) => {
    const key = normalizeKeyPart(query);
    const cached = geocodeCache.get(key);
    if (cached) return cached;

    const pending = (async () => {
      try {
        const result = await forwardGeocodeServer(query, 1);
        const first = result?.[0];
        if (!first || !Number.isFinite(first.lat) || !Number.isFinite(first.lon)) return null;
        return { lat: Number(first.lat), lng: Number(first.lon) };
      } catch {
        return null;
      }
    })();

    geocodeCache.set(key, pending);
    return pending;
  };

  const clear = (layer?: any | null) => {
    refreshToken += 1;
    markerCache.forEach((entry) => {
      try {
        layer?.removeLayer?.(entry.marker);
      } catch {
        try { entry.marker?.remove?.(); } catch {}
      }
    });
    markerCache.clear();
    try { layer?.clearLayers?.(); } catch {}
    marker = null;
  };

  const closePopup = () => {
    try { marker?.closePopup?.(); } catch {}
  };

  const refreshIcons = (isDarkMode: boolean) => {
    lastDarkMode = !!isDarkMode;
    markerCache.forEach((entry) => {
      try { entry.marker?.setIcon?.(createShopIcon(entry.itemIds.length, lastDarkMode)); } catch {}
    });
  };

  const getMarkers = () => Array.from(markerCache.values()).map((entry) => entry.marker).filter(Boolean);

  const resetForLayerRebuild = () => {
    refreshToken += 1;
    markerCache.clear();
    marker = null;
  };

  const sync = (options: BazaarLayerSyncOptions) => {
    const {
      map,
      layer,
      marketplaceItems,
      isDarkMode,
      userLatLng,
      clampPointToSafeBox,
      getOccupiedMapPoints,
      resolveMapMarkerCollision,
      onSelectListing,
      onOpenBazaar,
    } = options;

    lastDarkMode = !!isDarkMode;
    const groups = buildGroups(marketplaceItems);
    const token = ++refreshToken;

    if (!groups.size) {
      clear(layer);
      return () => {};
    }

    let cancelled = false;

    void (async () => {
      const resolvedGroups = (await Promise.all(
        Array.from(groups.values()).map(async (group) => {
          const result = await resolveGeocode(group.query);
          if (!result) return null;
          return { group, ...result };
        }),
      )).filter(Boolean) as ResolvedGroup[];

      if (cancelled || token !== refreshToken) return;
      if (!resolvedGroups.length) {
        clear(layer);
        return;
      }

      const anchorLatLng = userLatLng || map.getCenter?.();
      if (!anchorLatLng) return;

      const distanceSq = (lat: number, lng: number) => {
        const dLat = lat - Number(anchorLatLng.lat);
        const cosLat = Math.cos((Number(anchorLatLng.lat) * Math.PI) / 180);
        const dLng = (lng - Number(anchorLatLng.lng)) * cosLat;
        return dLat * dLat + dLng * dLng;
      };

      const nearestListings: ListingRow[] = resolvedGroups
        .flatMap(({ group, lat, lng }) =>
          group.items.map(({ id, item }) => ({
            id,
            item,
            label: group.label,
            locationKey: group.key,
            distanceScore: distanceSq(lat, lng),
          })),
        )
        .sort((a, b) => {
          if (a.distanceScore !== b.distanceScore) return a.distanceScore - b.distanceScore;
          return Number(b.item?.createdAt || 0) - Number(a.item?.createdAt || 0);
        });

      if (!nearestListings.length) {
        clear(layer);
        return;
      }

      const size = map.getSize?.();
      const userPoint = map.latLngToContainerPoint?.([
        Number(anchorLatLng.lat),
        Number(anchorLatLng.lng),
      ]);
      if (!size || !userPoint) return;

      const nearestResolvedGroup = [...resolvedGroups].sort(
        (a, b) => distanceSq(a.lat, a.lng) - distanceSq(b.lat, b.lng),
      )[0];
      const landAnchorPoint = nearestResolvedGroup
        ? map.latLngToContainerPoint?.([nearestResolvedGroup.lat, nearestResolvedGroup.lng])
        : null;

      let preferred = {
        x: Number(userPoint.x) - 92,
        y: Number(userPoint.y) + 18,
      };

      if (landAnchorPoint) {
        const dx = Number(landAnchorPoint.x) - Number(userPoint.x);
        const dy = Number(landAnchorPoint.y) - Number(userPoint.y);
        const distancePx = Math.hypot(dx, dy);
        if (Number.isFinite(distancePx) && distancePx > 0) {
          const travelPx = Math.min(distancePx, 150);
          const scale = travelPx / distancePx;
          preferred = {
            x: Number(userPoint.x) + dx * scale,
            y: Number(userPoint.y) + dy * scale,
          };
        }
      }

      preferred = clampPointToSafeBox(preferred, size);
      const occupied = getOccupiedMapPoints(map);
      const resolvedPoint = resolveMapMarkerCollision(preferred, occupied, size, {
        minDistance: 72,
        maxRadius: 56,
        seed: 271,
      });
      const markerLatLng = map.containerPointToLatLng?.([resolvedPoint.x, resolvedPoint.y]);
      if (!markerLatLng || cancelled || token !== refreshToken) return;

      markerCache.forEach((entry) => {
        try { layer.removeLayer?.(entry.marker); } catch {}
      });
      markerCache.clear();

      const itemIds = nearestListings.map((row) => row.id);
      const previewRows = nearestListings.slice(0, 3);
      const signature = `${Number(markerLatLng.lat).toFixed(5)}|${Number(markerLatLng.lng).toFixed(5)}|${itemIds.join(',')}`;
      const nextMarker = (L as any).marker(markerLatLng, {
        pane: 'markersPane',
        icon: createShopIcon(itemIds.length, lastDarkMode),
        keyboard: true,
        title: `Bazaar · ${itemIds.length} nearby listing${itemIds.length === 1 ? '' : 's'}`,
      }).addTo(layer);

      nextMarker.bindPopup?.(buildPopupHtml(previewRows, nearestListings.length, lastDarkMode), {
        maxWidth: 320,
        closeButton: true,
      });
      nextMarker.on?.('popupopen', () => {
        const popupEl = nextMarker.getPopup?.()?.getElement?.() as HTMLElement | null;
        if (!popupEl) return;

        popupEl.querySelectorAll<HTMLElement>('[data-roddo-bazaar-item]').forEach((el) => {
          el.onclick = () => {
            const itemId = el.getAttribute('data-roddo-bazaar-item') || '';
            const selected = nearestListings.find((row) => row.id === itemId);
            if (!selected) return;
            el.blur?.();
            nextMarker.closePopup?.();
            onSelectListing({ id: selected.id, item: selected.item });
          };
        });

        const openButton = popupEl.querySelector<HTMLElement>('[data-roddo-bazaar-open]');
        if (openButton) {
          openButton.onclick = () => {
            openButton.blur?.();
            nextMarker.closePopup?.();
            onOpenBazaar({ from: 'big_blue_bazaar_hub' });
          };
        }
      });

      markerCache.set('nearby', { marker: nextMarker, signature, itemIds });
      marker = nextMarker;
    })();

    return () => {
      cancelled = true;
      if (refreshToken === token) refreshToken += 1;
    };
  };

  const dispose = (layer?: any | null) => {
    clear(layer);
    geocodeCache.clear();
  };

  return {
    sync,
    clear,
    closePopup,
    refreshIcons,
    getMarkers,
    resetForLayerRebuild,
    dispose,
  };
}
